/* @jsxRuntime automatic */
/* @jsxImportSource @superweb/css */
import { useState } from "react";
import { Temporal } from "@js-temporal/polyfill";

import { useFlag } from "@superweb/flags";
import { useLocale } from "@superweb/intl";
import {
  Button,
  createComboBoxState,
  createDateFieldState,
  createTextFieldState,
} from "@superweb/ui";

import { useAction, useQuery } from "@fleet/api";

import { useLogAction } from "#internal/analytics";
import { useMessage } from "#internal/intl";
import {
  AppLayout,
  Form,
  NotFound,
  Offer,
  OfferDeprecated,
} from "#internal/ui";
import {
  useErrorMessages,
  useOnChangePartial,
  useOnShowErrors,
  useSubmitErrorMessages,
} from "#internal/utils";
import {
  searchParamTypes as t,
  useMakeLink,
  useNavigate,
  useSearchParams,
} from "#internal/router-new";
import SerbiaOfferPng from "#internal/images/serbia-offer.png";
import SerbiaOfferWebp from "#internal/images/serbia-offer.webp";
import SerbiaOfferAvif from "#internal/images/serbia-offer.avif";

import {
  DriverLicenseDetailsFields,
  type DriverLicenseDetailsState,
} from "../driver-license-details-fields";
import { useGetSteps } from "../steps";

const deliveryProfessionIds = new Set([
  "cargo_courier_on_foot",
  "cargo_courier_on_bicycle",
  "cargo_courier_on_scooter",
  "cargo_courier_on_moto",
  "cargo_courier_on_car",
  "cargo_courier_on_truck",
]);

const Content = ({
  refId,
  sessionId,
  specification,
  professionId,
  employmentType,
  leadId,
  previousStep,
  isMultipleProfessions,
  isMultipleEmploymentTypes,
  proId,
  isIframe,
}: {
  refId: string;
  sessionId: string;
  specification?: string;
  professionId?: string;
  employmentType?: "selfemployed" | "individual_entrepreneur" | "park_employee";
  leadId?: string;
  previousStep?: "profession" | "employment-type";
  isMultipleProfessions?: boolean;
  isMultipleEmploymentTypes?: boolean;
  proId?: boolean;
  isIframe: boolean;
}) => {
  const message = useMessage();
  const logActionSubmit = useLogAction("submit");
  const logActionInvalid = useLogAction("invalid");
  const logAction = useLogAction("contact");
  const locale = useLocale();
  const errorMessages = useErrorMessages();
  const flag = useFlag();
  const navigate = useNavigate();
  const makeLink = useMakeLink();

  const enableCountrySelection = flag({
    id: "72217bf4-53f4-4b1a-a3bb-cc2c0fe15465",
    description: "Enable driver license country selection",
  });

  const { response } = useQuery({
    url: "/fleet-forms/selfreg/v1/park/info",
    method: "GET",
    headers: {
      language: locale.toString(),
      "x-session-id": sessionId,
    },
    query: {
      ref_id: refId,
      profession_id: professionId,
      employment_type: employmentType,
    },
  });
  const parkInfo = response.status === 200 ? response.body : undefined;

  const { response: suggestResponse } = useQuery(
    proId
      ? {
          url: "/fleet-forms/selfreg/v1/suggest",
          method: "GET",
          headers: {
            language: locale.toString(),
          },
          query: {
            lead_id: leadId,
            ref_id: refId,
            employment_type: employmentType!,
            apply_auth: proId,
          },
        }
      : undefined,
  );
  const suggest =
    suggestResponse && suggestResponse.status === 200
      ? suggestResponse.body
      : undefined;

  const [formState, setFormState] = useState<DriverLicenseDetailsState>(() => {
    return {
      country: createComboBoxState(
        enableCountrySelection
          ? suggest?.driver_license?.country
            ? {
                value: {
                  key: suggest.driver_license.country,
                  label: suggest.driver_license.country,
                },
              }
            : {
                value: undefined,
                errorMessage: errorMessages.requiredField,
              }
          : undefined,
      ),
      number: createTextFieldState(
        suggest?.driver_license?.number
          ? {
              value: suggest.driver_license.number,
            }
          : {
              value: "",
              errorMessage: errorMessages.requiredField,
            },
      ),
      issue_date: createDateFieldState(
        suggest?.driver_license?.issue_date
          ? {
              value: Temporal.PlainDate.from(suggest.driver_license.issue_date),
            }
          : {
              errorMessage: errorMessages.requiredField,
            },
      ),
      expiry_date: createDateFieldState(
        suggest?.driver_license?.expiry_date
          ? {
              value: Temporal.PlainDate.from(
                suggest.driver_license.expiry_date,
              ),
            }
          : {
              errorMessage: errorMessages.requiredField,
            },
      ),
    };
  });

  const { action: actionSubmit, isFetching } = useAction({
    url: "/fleet-forms/selfreg/v1/submit",
    method: "POST",
  });

  const onChangePartial = useOnChangePartial(setFormState);
  const onShowErrors = useOnShowErrors(setFormState);
  const [formErrorMessage, setFormErrorMessage] = useState<string>();
  const submitErrorMessages = useSubmitErrorMessages(
    parkInfo?.country_code ?? "",
  );

  const onError = (code?: string) => {
    if (code) {
      setFormErrorMessage(submitErrorMessages[code] ?? errorMessages.default);
    } else {
      setFormErrorMessage(errorMessages.default);
    }
  };

  const [licenseTemplates, setLicenseTemplates] = useState<string[]>(
    parkInfo?.license_number_templates ?? [],
  );

  const { action: getLicenseTemplatesData } = useAction({
    url: "/fleet-forms/selfreg/v1/license/number-templates",
    method: "GET",
  });

  const title = message({
    id: "123e4c09-04c1-4a82-8442-46300ea49d81",
    context: "Yango forms. Driver details form. Header title",
    default: "Driver's license details",
  });

  const professionSettings = parkInfo?.profession_settings_list.find(
    (profession) => profession.profession_id === professionId,
  );
  const getSteps = useGetSteps();
  const { stepsCount, currentStep, nextStep } = getSteps({
    isMultipleProfessions,
    isMultipleEmploymentTypes,
    isLoginWithPro: proId,
    formRequirements:
      professionSettings?.form_requirements ?? parkInfo?.form_requirements,
    currentStep: "license",
    employmentType,
  });

  if (!parkInfo) {
    return <NotFound />;
  }

  const searchParams = new URLSearchParams({
    ref_id: refId,
    ...(sessionId ? { sessionId } : undefined),
    ...(specification ? { specification } : undefined),
    ...(professionId ? { profession_id: professionId } : undefined),
    ...(employmentType ? { employment_type_id: employmentType } : undefined),
    ...(leadId ? { lead_id: leadId } : undefined),
  });

  const isDelivery =
    !isMultipleProfessions &&
    deliveryProfessionIds.has(
      parkInfo.profession_settings_list[0]?.profession_id ?? "",
    );
  const offerType = isMultipleProfessions
    ? "multiple"
    : isDelivery
      ? "delivery"
      : "taxi";

  const enableNewOffer = flag({
    id: "8ed16f3a-25d7-4455-8c23-a623eb0cd824",
    description: "Enable new offer",
  });
  const enableSerbiaOfferImage =
    parkInfo.park_id === "1a1d6c5f1ad7423481a1bc8a8c5d09db";

  return (
    <AppLayout
      title={isIframe ? undefined : title}
      partnerName={parkInfo.partner_name ?? ""}
      content={
        <Form
          header={{
            title,
            ...(currentStep && {
              step: {
                current: currentStep,
                total: stepsCount,
              },
            }),
            ...(previousStep
              ? {
                  previousStep: makeLink(`/${previousStep}?${searchParams}`),
                }
              : {}),
          }}
          content={
            <DriverLicenseDetailsFields
              state={formState}
              onChangePartial={onChangePartial}
              countryCode={parkInfo.country_code}
              countryId={parkInfo.country_id}
              licenseTemplates={licenseTemplates}
            />
          }
          onSubmit={() => {
            onShowErrors();
            const hasErrors = Object.keys(formState).some(
              (field) =>
                formState[field as keyof DriverLicenseDetailsState]
                  .errorMessage !== undefined,
            );

            if (hasErrors) {
              if (
                (licenseTemplates.length > 0 &&
                  formState.number.errorMessage ===
                    errorMessages.invalidDriverLicenseWithTemplates(
                      licenseTemplates.join(", "),
                    )) ||
                formState.number.errorMessage ===
                  errorMessages.invalidDriverLicense
              ) {
                logActionInvalid("invalid_driver_license", {
                  onlyYM: true,
                });
              }

              if (
                formState.expiry_date.errorMessage === errorMessages.invalidDate
              ) {
                logActionInvalid("invalid_expiry_date", {
                  onlyYM: true,
                });
              }

              if (
                formState.issue_date.errorMessage === errorMessages.invalidDate
              ) {
                logActionInvalid("invalid_issue_date", {
                  onlyYM: true,
                });
              }

              return;
            }

            actionSubmit(
              {
                headers: {
                  "x-submission-id": sessionId,
                  language: locale.toString(),
                  "content-type": "application/json",
                },
                query: { ref_id: refId, apply_auth: proId },
                body: {
                  license: {
                    number: formState.number.value,
                    issue_date: String(formState.issue_date.value?.toString()),
                    expiry_date: String(
                      formState.expiry_date.value?.toString(),
                    ),
                    country_id:
                      formState.country.value?.key ?? parkInfo.country_id,
                  },
                },
              },
              {
                onResponse: (res) => {
                  if (res.status === 204) {
                    logActionSubmit("submit_license");
                    const searchParams = new URLSearchParams({
                      ref_id: refId,
                      ...(sessionId ? { sessionId } : undefined),
                      ...(specification ? { specification } : undefined),
                      ...(professionId
                        ? { profession_id: professionId }
                        : undefined),
                      ...(employmentType
                        ? { employment_type_id: employmentType }
                        : undefined),
                      ...(leadId ? { lead_id: leadId } : undefined),
                    });
                    navigate(`/${nextStep ?? "download-app"}?${searchParams}`);
                    return;
                  }

                  switch (res.body.code) {
                    case "invalid_driver_license": {
                      logActionInvalid("invalid_driver_license", {
                        onlyYM: true,
                      });
                      if (enableCountrySelection) {
                        getLicenseTemplatesData(
                          {
                            headers: { language: locale.toString() },
                            query: {
                              country_id:
                                formState.country.value?.key ??
                                parkInfo.country_id,
                            },
                          },
                          {
                            onResponse: (res) => {
                              let templates: string[] = [];
                              if (res.status === 200) {
                                setLicenseTemplates(
                                  res.body.license_number_templates,
                                );
                                templates = res.body.license_number_templates;
                              }
                              onChangePartial({
                                number: {
                                  ...formState.number,
                                  errorMessage:
                                    templates.length > 0
                                      ? errorMessages.invalidDriverLicenseWithTemplates(
                                          templates.join(", "),
                                        )
                                      : errorMessages.invalidDriverLicense,
                                  errorVisible: true,
                                },
                              });
                            },
                          },
                        );
                      } else {
                        onChangePartial({
                          number: {
                            ...formState.number,
                            errorMessage:
                              licenseTemplates.length > 0
                                ? errorMessages.invalidDriverLicenseWithTemplates(
                                    licenseTemplates.join(", "),
                                  )
                                : errorMessages.invalidDriverLicense,
                            errorVisible: true,
                          },
                        });
                      }
                      break;
                    }
                    case "invalid_issue_date": {
                      logActionInvalid("invalid_issue_date", { onlyYM: true });
                      onChangePartial({
                        issue_date: {
                          ...formState.issue_date,
                          errorMessage: errorMessages.invalidDate,
                          errorVisible: true,
                        },
                      });
                      break;
                    }
                    case "invalid_expiry_date": {
                      logActionInvalid("invalid_expiry_date", {
                        onlyYM: true,
                      });
                      onChangePartial({
                        expiry_date: {
                          ...formState.expiry_date,
                          errorMessage: errorMessages.invalidDate,
                          errorVisible: true,
                        },
                      });
                      break;
                    }
                    default: {
                      onError(res.body.code);
                      break;
                    }
                  }
                },
              },
            );
          }}
          footer={{
            main: (
              <Button
                variant="action"
                type="submit"
                shape="squircle"
                progress={isFetching}
                text={message({
                  id: "7be6d017-063c-4901-ae94-5594990837ce",
                  context: "Yango forms. Submit button text",
                  default: "Save",
                })}
                size="l"
              />
            ),
            errorMessage: formErrorMessage,
          }}
        />
      }
      offer={
        isIframe ? undefined : enableNewOffer ? (
          <Offer
            offerText={parkInfo.promo_text}
            image={
              enableSerbiaOfferImage
                ? {
                    avif: SerbiaOfferAvif,
                    png: SerbiaOfferPng,
                    webp: SerbiaOfferWebp,
                    height: "130px",
                    width: "178px",
                    padding: "24px",
                  }
                : undefined
            }
            type={offerType}
            onPhonePress={() =>
              logAction("phone_click_v2", {
                params: { stage: "register" },
              })
            }
            onMessengerPress={() =>
              logAction("messenger_click_v2", {
                params: { stage: "register" },
              })
            }
            phone={parkInfo.phone}
            messageLink={parkInfo.message_link}
          />
        ) : (
          <OfferDeprecated
            parkId={parkInfo.park_id}
            offerText={parkInfo.promo_text}
            isDelivery={specification === "delivery"}
            onPhonePress={() =>
              logAction("phone_click_v2", { params: { stage: "details" } })
            }
            onMessengerPress={() =>
              logAction("messenger_click_v2", { params: { stage: "details" } })
            }
            phone={parkInfo.phone}
            messageLink={parkInfo.message_link}
          />
        )
      }
    />
  );
};
export const LicenseForm = ({ isIframe }: { isIframe: boolean }) => {
  const [
    {
      ref_id: refId,
      sessionId,
      specification,
      profession_id: professionId,
      employment_type_id: employmentType,
      lead_id: leadId,
      previous_step: previousStep,
      is_multiple_professions: isMultipleProfessions,
      is_multiple_employment_types: isMultipleEmploymentTypes,
      pro_id: proId,
    },
  ] = useSearchParams({
    ref_id: t.string,
    sessionId: t.string,
    specification: t.string,
    profession_id: t.string,
    employment_type_id: t.enumString([
      "selfemployed",
      "individual_entrepreneur",
      "park_employee",
    ] as const),
    lead_id: t.string,
    previous_step: t.enumString(["profession", "employment-type"] as const),
    is_multiple_professions: t.boolean,
    is_multiple_employment_types: t.boolean,
    pro_id: t.boolean,
  });
  if (!refId || !sessionId) {
    return <NotFound />;
  }

  return (
    <Content
      refId={refId}
      sessionId={sessionId}
      specification={specification}
      professionId={professionId}
      employmentType={employmentType}
      leadId={leadId}
      previousStep={previousStep}
      isMultipleProfessions={isMultipleProfessions}
      isMultipleEmploymentTypes={isMultipleEmploymentTypes}
      proId={proId}
      isIframe={isIframe}
    />
  );
};
